// src/components/form/DynamicUserForm.js
/**
 * DynamicUserForm
 * ---------------
 * Multi-step questionnaire.
 *
 * Responsibilities
 * • Filters the global `questions` array against current answers + user data.
 * • Generates a step list (`single`, `group`, `review`) via `buildSteps()`.
 * • Performs per-step validation before allowing Next.
 * • On the final “Confirm” step:
 *      – uploads answers as JSON to Cloud Storage → `user_metadata/{uid}/…`
 *      – falls back to `localStorage` when offline or on upload error.
 * • Redirects to `/recording` once data is safely persisted.
 *
 * Notes
 * • Pure UI logic is delegated to smaller components/helpers so this file
 *   stays focused on orchestration.
 * • Hook calls are grouped by purpose: derived state → effects → handlers.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import { getAuth } from "firebase/auth";

import { useAuth } from "../../context/AuthContext";
import { firebaseApp } from "../../firebase/firebaseApp";

import QuestionStep from "./QuestionStep";
import GroupedQuestionStep from "./GroupedQuestionStep";
import ReviewStep from "./ReviewStep";

import { questions } from "../../utils/form/formQuestions";
import { isQuestionValid } from "../../utils/form/isQuestionValid";
import {
  buildSteps,
  filterQuestions,
  rangeDefaults,
} from "../../utils/form/formRuntime";
import { uploadMetadata } from "../../utils/firebase/uploadMetadata";
import FormNavButtons from "./FormNavButtons";

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
/**
 * @param {{ initialData?: Record<string, any> }} props
 */
function DynamicUserForm({ initialData = {} }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ----------------------------- state ----------------------------- */
  // ---------- initial answers ----------
  const [formData, setFormData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);

  /* --------------------------- selectors --------------------------- */
  const filtered = useMemo(
    () => filterQuestions(questions, formData, user),
    [formData, user]
  );
  const steps = useMemo(() => buildSteps(filtered), [filtered]);
  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;

  /* ------------------- initialise range defaults ------------------ */
  useEffect(() => {
    const defaults = rangeDefaults(filtered, formData);
    if (Object.keys(defaults).length) {
      setFormData((p) => ({ ...p, ...defaults }));
    }
  }, [filtered]);

  /* --------------------------- handlers --------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  /** True when every required field in the current step is valid. */
  const validateStep = () => {
    const step = steps[currentStep];
    if (step.type === "single") return isQuestionValid(step.question, formData);
    if (step.type === "group")
      return !step.questions.some((q) => !isQuestionValid(q, formData));
    return true; // review step
  };

  const handleNext = useCallback(
    /** @param {React.FormEvent | undefined} e */
    async (e) => {
      e?.preventDefault();

      /* ── Final step → persist answers ───────────────────────────── */
      if (currentStep === totalSteps - 1) {
        try {
          const { currentUser } = getAuth(firebaseApp);
          if (!currentUser) throw new Error("No authenticated user");
          await uploadMetadata(currentUser.uid, formData);
        } catch (err) {
          // Offline or quota exceeded – fall back so the user can proceed.
          console.error(
            "Metadata upload failed, writing to localStorage instead:",
            err
          );
          localStorage.setItem("formMetadata", JSON.stringify(formData));
        }

        navigate("/recording", { replace: true });
        return;
      }

      /* ── Validate current step before advancing ─────────────────── */
      if (!validateStep()) {
        alert("Please complete all required fields before proceeding.");
        return;
      }

      setCurrentStep((s) => s + 1);
    },
    [currentStep, totalSteps, formData, navigate] // deps
  );

  const handlePrev = (e) => {
    e.preventDefault();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  /* ------------- auto-fill when returning user -------------------- */
  useEffect(() => {
    if (user.returning) {
      const stored = localStorage.getItem("autoFillData");
      if (stored) setFormData((p) => ({ ...p, ...JSON.parse(stored) }));
    }
  }, [user.returning]);

  /* ---- edge case: zero questions after filtering → skip form ----- */
  useEffect(() => {
    if (!filtered.length) {
      localStorage.setItem("formMetadata", JSON.stringify(formData));
      navigate("/recording");
    }
  }, [filtered, formData, navigate]);

  /* ------------------------ render helpers ------------------------ */
  const renderStep = () => {
    const step = steps[currentStep];
    switch (step.type) {
      case "single":
        return (
          <QuestionStep
            question={step.question}
            value={formData[step.question.id]}
            onChange={handleChange}
            autoFocus
            onEnter={handleNext}
          />
        );
      case "group":
        return (
          <GroupedQuestionStep
            questions={step.questions}
            formData={formData}
            onChange={handleChange}
            onEnter={handleNext}
          />
        );
      default:
        return <ReviewStep questions={filtered} formData={formData} />;
    }
  };

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="user-form p-4 w-full mx-auto flex flex-col justify-between gap-4">
      {/* progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        className="rounded-full"
        sx={{
          backgroundColor: "#B776B9",
          "& .MuiLinearProgress-bar": { backgroundColor: "#852170" },
        }}
      />

      {renderStep()}

      {/* nav buttons */}
      <FormNavButtons
        hasPrev={currentStep > 0}
        onPrev={handlePrev}
        onNext={handleNext}
        isLast={currentStep === totalSteps - 1}
      />
    </div>
  );
}

export default DynamicUserForm;
